// components/crm/CRMAnalytics.jsx
import {
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  UserGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/* ── Palette ──────────────────────────────────────────────────────────────── */
const STATUS_COLORS = {
  new:                  '#6366f1',
  inprogress:           '#f59e0b',
  attended:             '#06b6d4',
  interested:           '#8b5cf6',
  not_responding:       '#94a3b8',
  not_interested:       '#ef4444',
  enrolled_with_others: '#f97316',
  converted:            '#10b981',
  enrolled:             '#059669',
  lost:                 '#dc2626',
  open:                 '#6366f1',
  contacted:            '#f59e0b',
  demo_scheduled:       '#06b6d4',
  awaiting_decision:    '#8b5cf6',
};

const SOURCE_COLORS = ['#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16'];
const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

/* ── KPI Card ─────────────────────────────────────────────────────────────── */
const KPICard = ({ title, value, sub, icon: Icon, color, bg }) => (
  <Card className="relative overflow-hidden border-0 shadow-md">
    <div className="absolute inset-0 opacity-[0.05]" style={{ background: color }} />
    <CardContent className="p-5 flex items-center gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm" style={{ background: color }}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

/* ── Custom Tooltip ───────────────────────────────────────────────────────── */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-xl text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

/* ── Main ─────────────────────────────────────────────────────────────────── */
export default function CRMAnalytics({ stats, taskStats }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-pulse">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="h-24 border-0 shadow-sm"><CardContent className="h-full bg-gray-100 rounded-xl" /></Card>
        ))}
      </div>
    );
  }

  const {
    totalLeads = 0, convertedLeads = 0, lostLeads = 0, activeLeads = 0,
    conversionRate = 0,
    monthlyTrend = [], sourceTrend = [], priorityTrend = [],
    courseTrend = [], funnelData = [], statusCounts = {},
  } = stats;

  // Build status pie data
  const statusPieData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name:  k.replace(/_/g, ' '),
      value: v,
      fill:  STATUS_COLORS[k] || '#94a3b8',
    }))
    .sort((a, b) => b.value - a.value);

  // Priority pie data with colors
  const priorityPieData = priorityTrend.map(p => ({
    name:  p.priority,
    value: p.count,
    fill:  PRIORITY_COLORS[p.priority] || '#94a3b8',
  }));

  return (
    <div className="space-y-5">
      {/* ── KPI Row ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard title="Total Leads"     value={totalLeads}      sub="all time"            icon={UserGroupIcon}         color="#6366f1" />
        <KPICard title="Active Leads"    value={activeLeads}     sub="in pipeline"         icon={ArrowTrendingUpIcon}   color="#f59e0b" />
        <KPICard title="Converted"       value={convertedLeads}  sub={`${conversionRate}% rate`} icon={CheckCircleIcon} color="#10b981" />
        <KPICard title="Lost"            value={lostLeads}       sub="not interested / lost" icon={XCircleIcon}         color="#ef4444" />
        <KPICard title="Tasks Total"     value={taskStats?.totalTasks     || 0} sub="all tasks"     icon={FunnelIcon}  color="#8b5cf6" />
        <KPICard title="Overdue Tasks"   value={taskStats?.overdueTasks   || 0} sub="need attention" icon={ExclamationCircleIcon} color="#f97316" />
      </div>

      {/* ── Row 1: Monthly Trend + Status Pie ───────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Trend */}
        <Card className="lg:col-span-2 shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-4 w-4 text-indigo-500" />
              Monthly Lead Trend (Last 12 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyTrend.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthlyTrend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="leads"     stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} name="New Leads" />
                  <Line type="monotone" dataKey="converted" stroke="#10b981" strokeWidth={2}   dot={{ r: 3 }} name="Converted" strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Status Pie */}
        <Card className="shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-amber-500" />
              Lead Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusPieData.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data yet</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                    >
                      {statusPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend below */}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 justify-center">
                  {statusPieData.slice(0, 8).map((d, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] text-gray-600">
                      <span className="h-2 w-2 rounded-full inline-block" style={{ background: d.fill }} />
                      {d.name} ({d.value})
                    </span>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Row 2: Source Bar + Course Bar ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Source Breakdown */}
        <Card className="shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <UserGroupIcon className="h-4 w-4 text-blue-500" />
              Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sourceTrend.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No source data</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={sourceTrend} margin={{ top: 4, right: 8, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="source" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Leads" radius={[4, 4, 0, 0]} maxBarSize={50}>
                    {sourceTrend.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Course Interest */}
        <Card className="shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-4 w-4 text-purple-500" />
              Leads by Course Interest
            </CardTitle>
          </CardHeader>
          <CardContent>
            {courseTrend.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No course data</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={courseTrend} layout="vertical" margin={{ top: 4, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <YAxis dataKey="course" type="category" tick={{ fontSize: 10 }} width={60} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]} maxBarSize={24}>
                    {courseTrend.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[(i + 2) % SOURCE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Row 3: Pipeline Funnel + Priority Pie ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pipeline Funnel */}
        <Card className="shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-emerald-500" />
              Lead Pipeline Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnelData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No funnel data</div>
            ) : (
              <div className="space-y-2 pt-1">
                {funnelData.map((d, i) => {
                  const pct = totalLeads > 0 ? Math.round((d.count / totalLeads) * 100) : 0;
                  const colors = Object.values(STATUS_COLORS);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-32 capitalize truncate text-right">{d.status}</span>
                      <div className="flex-1 h-6 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                          style={{
                            width: `${Math.max(pct, 3)}%`,
                            background: colors[i % colors.length],
                          }}
                        >
                          <span className="text-[10px] font-bold text-white leading-none">{d.count}</span>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-500 w-10 text-right">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Priority + Task breakdown */}
        <Card className="shadow-sm border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
              Priority Distribution & Task Status
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Priority pie */}
            {priorityPieData.length > 0 && (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie data={priorityPieData} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                      {priorityPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-col gap-2">
                  {priorityPieData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full shrink-0" style={{ background: d.fill }} />
                      <span className="text-xs capitalize text-gray-600">{d.name} priority</span>
                      <span className="ml-auto text-xs font-bold text-gray-800">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Task status row */}
            {taskStats && (
              <div className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-3">
                {[
                  { label: 'Total',     value: taskStats.totalTasks     || 0, color: '#6366f1' },
                  { label: 'Completed', value: taskStats.completedTasks || 0, color: '#10b981' },
                  { label: 'Overdue',   value: taskStats.overdueTasks   || 0, color: '#ef4444' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-gray-100 p-3 text-center">
                    <p className="text-xl font-bold" style={{ color }}>{value}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{label} Tasks</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
